import './style.scss';

import { faCheck, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Button,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Checkbox,
} from '@mui/material';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

interface ISubListProperties {
  name: string;
  key: any;
  startIcon?: ReactNode;
  items: (IItemProperties | ISubListProperties)[];
}
interface IItemProperties {
  name: string;
  value: any;
  startIcon?: ReactNode;
}

interface ISelectProperties {
  values: (IItemProperties | ISubListProperties)[];
  defaultValue: IItemProperties | IItemProperties[];
  onChange: (value: any) => void;
  className?: string;
  label?: string;
  customLabel?: ReactNode;
  disabled?: boolean;
  multiSelect?: boolean;
}

export default function Select(properties: ISelectProperties) {
  const {
    defaultValue,
    values,
    onChange,
    className,
    label,
    customLabel,
    disabled = false,
    multiSelect = false,
  } = properties;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [selected, setSelected] = useState<IItemProperties[]>(
    Array.isArray(defaultValue) ? defaultValue : [defaultValue],
  );
  const [openedListKey, setOpenedListKey] = useState('');

  useEffect(() => {
    const findDefaultSubmenuKey = (
      items: (IItemProperties | ISubListProperties)[],
      defaultValue: IItemProperties | IItemProperties[],
    ): string | undefined => {
      const defaultValues = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
      for (const item of items) {
        if (isList(item)) {
          const subList = item as ISubListProperties;
          if (
            subList.items.some((subItem) =>
              defaultValues.some(
                (defaultVal) => (subItem as IItemProperties).value === defaultVal.value,
              ),
            )
          ) {
            return subList.key;
          }
        }
      }
      return undefined;
    };

    const defaultSubmenuKey = findDefaultSubmenuKey(values, defaultValue);
    if (defaultSubmenuKey) {
      setOpenedListKey(defaultSubmenuKey);
    }
  }, [values, defaultValue]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;

    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setSelected(Array.isArray(defaultValue) ? defaultValue : [defaultValue]);
  }, [defaultValue]);

  const styleObj = {
    borderRadius: 0,
    '&:hover': {
      borderRadius: 0,
    },
    '&:active': {
      borderRadius: 0,
    },
  };

  const isList = (obj: any) => {
    return 'items' in obj;
  };

  const getColor = (level: number) => {
    const colors = ['#1D2F39', '#172730', '#102028'];

    if (level <= colors.length) {
      return colors[level];
    }

    return 'red';
  };

  const renderList = useMemo(() => {
    const handleChange = (value: IItemProperties) => {
      if (multiSelect) {
        const isSelected = selected.some((item) => item.value === value.value);
        let updatedSelection = isSelected
          ? selected.filter((item) => item.value !== value.value)
          : [...selected, value];

        if (updatedSelection.length === 0) {
          updatedSelection = [value];
        }

        setSelected(updatedSelection);
        onChange(updatedSelection.map((item) => item.value));
      } else {
        setSelected([value]);
        onChange(value.value);
        handleClose();
      }
    };

    const renderItem = (item: IItemProperties, level: number) => {
      const isSelected = selected.some((sel) => sel.value === item.value);
      return (
        <MenuItem
          key={item.value}
          selected={!multiSelect && isSelected}
          onClick={() => handleChange(item)}
          sx={{
            // backgroundColor: getColor(level),
            paddingLeft: `${(level + 1) * 16}px`,
            paddingY: '10px',
          }}
        >
          {multiSelect && (
            <Checkbox
              checked={isSelected}
              onChange={() => handleChange(item)}
              sx={{ padding: 0, marginRight: '8px' }}
            />
          )}
          {item.startIcon && <ListItemIcon>{item.startIcon}</ListItemIcon>}
          <ListItemText disableTypography>{item.name}</ListItemText>
          {!multiSelect && (
            <ListItemIcon className="selected-check-icon">
              {isSelected && <FontAwesomeIcon icon={faCheck} />}
            </ListItemIcon>
          )}
        </MenuItem>
      );
    };

    const renderSubList = (list: ISubListProperties, level: number) => {
      const isOpen = openedListKey.includes(list.key);

      const handleClickList = (key: string) => {
        let newKey = '';

        if (key === openedListKey) {
          const lastUndescore = openedListKey.lastIndexOf('_');

          if (lastUndescore > 0) {
            newKey = openedListKey.substring(lastUndescore + 1, openedListKey.length);
          }
        } else if (!openedListKey.includes(key)) {
          newKey = key;
        }

        setOpenedListKey(newKey);
      };

      return (
        <div key={list.key}>
          <ListItemButton
            onClick={() => handleClickList(list.key)}
            sx={{
              backgroundColor: getColor(level),
              paddingLeft: `${(level + 1) * 16}px`,
            }}
          >
            <ListItemText primary={list.name} />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {list.items.map((item) => {
                if (isList(item)) {
                  return renderSubList(item as ISubListProperties, level + 1);
                } else {
                  return renderItem(item as IItemProperties, level + 1);
                }
              })}
            </List>
          </Collapse>
        </div>
      );
    };

    return values.map((item: IItemProperties | ISubListProperties) => {
      if (!isList(item)) {
        return renderItem(item as IItemProperties, 0);
      } else {
        return renderSubList(item as ISubListProperties, 0);
      }
    });
  }, [onChange, openedListKey, selected, values, multiSelect]);

  return (
    <div className={`select-container ${className}`}>
      {!!label && <span className="label">{label}</span>}
      <Button
        onClick={handleClick}
        aria-controls={open ? 'menu' : undefined}
        aria-haspopup="true"
        disableRipple
        sx={styleObj}
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        className={open ? 'selector-button selector-button-open ' : ' selector-button'}
        endIcon={
          open ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />
        }
      >
        <span className="select-name">
          {customLabel ||
            (multiSelect
              ? `${selected[0]?.name}${selected.length > 1 ? `..., ${selected.length - 1} more` : ''}`
              : selected[0]?.name)}
        </span>
      </Button>

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={`menu-select-container ${className}`}
        PaperProps={{
          className: className ? `${className}-paper` : undefined,
          style: { minWidth: anchorEl ? (anchorEl as HTMLElement).offsetWidth : undefined },
        }}
      >
        {renderList}
      </Menu>
    </div>
  );
}
